import React from "react";
import { Formik } from "formik";
import * as _ from "underscore";
import {
  Button,
  ButtonSet,
  Form,
  FormGroup,
  Loading,
  Select,
  SelectItem,
  TextInput,
} from "carbon-components-react";
import { LoadingState } from "underwriter_dashboard/shared/types";

import { EditingState } from "web_modules/shared/BaseTable";

import { validationSchema } from "./FormValues";

interface Props {
  formState: any;
  powerOfAttorneyOptions: any;
  onSubmit: any;
  editingMode: EditingState;
  onRequestClose: Function;
  loading: LoadingState;
}

const AddPowerOfAttorneyForm: React.FC<Props> = ({
  formState,
  powerOfAttorneyOptions,
  onSubmit,
  editingMode,
  onRequestClose,
  loading,
}) => {
  const submitForm = async (values, { setSubmitting, setStatus, resetForm, setErrors }) => {
    try {
      await onSubmit(values);

      // after submit
      resetForm({});
      setStatus({ success: true });
    } catch (error) {
      // on error
      setStatus({ success: false });
      setSubmitting(false);
      setErrors({ submit: error.message });

      throw error;
    }
  };

  const borrowerOptions = () => {
    return _.omit(powerOfAttorneyOptions, [
      "titleOnlyName1",
      "titleOnlyName2",
      "titleOnlyName3",
      "titleOnlyName4",
      "other",
    ]);
  };

  const authorizedAgentOptions = () => {
    return _.omit(powerOfAttorneyOptions, value => value === "");
  };

  return (
    <>
      <Loading active={loading === LoadingState.ACTIVE} />
      <Formik
        enableReinitialize
        onSubmit={submitForm}
        initialValues={formState}
        powerOfAttorneyOptions={powerOfAttorneyOptions}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          resetForm,
          isSubmitting,
        }) => {
          const { borrower_principal, authorized_agent, other_authorized_agent_name } = values;
          return (
            <Form
              onSubmit={e => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <FormGroup legendText="">
                <Select
                  id="borrower-principal"
                  name="borrower_principal"
                  value={borrower_principal}
                  invalid={!!errors.borrower_principal && touched.borrower_principal}
                  invalidText={errors.borrower_principal}
                  labelText="Borrower / Principal *"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  size="sm"
                >
                  <SelectItem key="no-borrower" text="Select a borrower" value="" />
                  {Object.keys(_.omit(borrowerOptions(), authorized_agent)).map(b => (
                    <SelectItem key={b} text={borrowerOptions()[b]} value={b} />
                  ))}
                </Select>

                <div style={{ marginTop: "20px" }}>
                  <Select
                    id="authorized-agent"
                    name="authorized_agent"
                    value={authorized_agent}
                    invalid={!!errors.authorized_agent && touched.authorized_agent}
                    invalidText={errors.authorized_agent}
                    labelText="Authorized Agent *"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    size="sm"
                  >
                    <SelectItem key="no-agent" text="Select an authorized agent" value="" />
                    {Object.keys(_.omit(authorizedAgentOptions(), borrower_principal)).map(b => (
                      <SelectItem key={b} text={authorizedAgentOptions()[b]} value={b} />
                    ))}
                  </Select>
                </div>

                {authorized_agent === "other" && (
                  <div style={{ marginTop: "20px" }}>
                    <TextInput
                      id="other-authorized-agent-name"
                      name="other_authorized_agent_name"
                      size="sm"
                      value={other_authorized_agent_name}
                      labelText="Other Authorized Agent Name *"
                      type="text"
                      invalid={
                        !!errors.other_authorized_agent_name && touched.other_authorized_agent_name
                      }
                      invalidText={errors.other_authorized_agent_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                )}
              </FormGroup>
              <ButtonSet>
                <Button
                  kind="secondary"
                  size="lg"
                  onClick={() => {
                    resetForm();
                    onRequestClose();
                  }}
                  style={{ maxWidth: "50%" }}
                  disable={isSubmitting ? "true" : "false"}
                >
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  size="lg"
                  type="submit"
                  style={{ maxWidth: "50%" }}
                  disable={isSubmitting ? "true" : "false"}
                >
                  {editingMode === EditingState.CREATE ? "Save" : "Update"}
                </Button>
              </ButtonSet>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default AddPowerOfAttorneyForm;
